import { yupResolver } from '@hookform/resolvers/yup'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import {
	DESCRIPTION_MAX_LENGTH,
	TITLE_MAX_LENGTH,
} from 'constants/validationConstants'
import { RhcField } from 'features/generator/components/RhcField'
import { PrimitiveAtom, useAtom, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { TUploadedMedia } from 'services/mediaStorage'
import { validateContentForm } from 'utils/formUtils'
import { maxLength } from 'utils/inputChangeUtils'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { markContentTouched } from '../../generatorState'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { ImageUpload } from './ImageUpload'
import {
	getRestaurantInfoFormSchema,
	TRestaurantInfoFormFields,
} from './menuFormModel'

export const RESTAURANT_INFO_FORM_ID = 'restaurantInfo'

export const restaurantImageFileAtom = atomWithReset<File | null>(null)
export const restaurantImageAtom = atomWithReset<TUploadedMedia | null>(null)
export const restaurantNameAtom = atomWithReset('')
export const restaurantDescriptionAtom = atomWithReset('')

export const resetRestaurantInfoForm = () => {
	getStore().set(restaurantImageFileAtom, RESET)
	getStore().set(restaurantImageAtom, RESET)
	getStore().set(restaurantNameAtom, RESET)
	getStore().set(restaurantDescriptionAtom, RESET)
}

export const RestaurantInfo = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const setRestaurantImage = useSetAtom(restaurantImageAtom)
	const [restaurantImageFile, setRestaurantImageFile] = useAtom(
		restaurantImageFileAtom,
	)

	const form = useForm({
		defaultValues: {
			name: getStore().get(restaurantNameAtom),
			description: getStore().get(restaurantDescriptionAtom),
		},
		resolver: yupResolver(
			getRestaurantInfoFormSchema(t),
		) as unknown as Resolver<TRestaurantInfoFormFields>,
		mode: 'onTouched',
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const { handleSubmit } = form

	const handleImageUpload = (
		uploadedFile: TUploadedMedia,
		originalFile: File,
	) => {
		setRestaurantImage(uploadedFile)
		setRestaurantImageFile(originalFile)
	}

	const handleImageUploadStart = (file: File) => {
		markContentTouched()
		setRestaurantImageFile(file)
	}

	const handleImageDelete = () => {
		setRestaurantImage(null)
		setRestaurantImageFile(null)
	}

	useImperativeHandle(
		ref,
		() => ({
			validate: () =>
				validateContentForm((onValid, onInvalid) =>
					handleSubmit(onValid, async (errors) => {
						onInvalid(errors)
					}),
				),
		}),
		[handleSubmit],
	)

	return (
		<div className="flex flex-col gap-6 tablet:gap-8">
			<ImageUpload
				initialFileList={
					restaurantImageFile ? [restaurantImageFile] : []
				}
				onUploadStart={handleImageUploadStart}
				onUpload={handleImageUpload}
				onDelete={handleImageDelete}
			/>
			<div className="flex w-full flex-col gap-4 tablet:flex-row tablet:gap-12">
				<RhcField
					type="text"
					form={form}
					path="name"
					fieldName="restaurant_name"
					atom={
						restaurantNameAtom as PrimitiveAtom<string | undefined>
					}
					onChangeTransform={(event) =>
						maxLength(TITLE_MAX_LENGTH)(event)
					}
					className="w-full"
					label={t(
						'generator__content_form_section__menu__restaurant_info__name',
					)}
					placeholder={t(
						'generator__content_form_section__menu__restaurant_info__name__placeholder',
					)}
				/>
				<RhcField
					type="text"
					form={form}
					path="description"
					fieldName="restaurant_description"
					atom={
						restaurantDescriptionAtom as PrimitiveAtom<
							string | undefined
						>
					}
					onChangeTransform={(event) =>
						maxLength(DESCRIPTION_MAX_LENGTH)(event)
					}
					className="w-full"
					label={t(
						'generator__content_form_section__menu__restaurant_info__description',
					)}
					placeholder={t(
						'generator__content_form_section__menu__restaurant_info__description__placeholder',
					)}
				/>
			</div>
		</div>
	)
})

RestaurantInfo.displayName = 'RestaurantInfo'

export const restaurantInfoFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: RESTAURANT_INFO_FORM_ID,
	title: t('generator__content_form_section__restaurant_info__title'),
	description: t(
		'generator__content_form_section__restaurant_info__description',
	),
	icon: <LineInfoCircle className="text-info-color fill-info-color" />,
	Content: RestaurantInfo,
})
